/**
 * NIGHTBOUND - the art asset manifest.
 *
 * Artwork is *never* imported by React components. Instead every visual is
 * described here as an ordered list of candidate URLs, and the renderer probes
 * them in order. That means:
 *
 *   * dropping `public/assets/creatures/dog/dog_sleeping.webp` into place makes
 *     the game use it immediately - no code change, no rebuild of game logic;
 *   * a missing state degrades gracefully instead of crashing;
 *   * temporary placeholder art can be replaced file-by-file as real art
 *     arrives.
 *
 * The reference sheet in `References/` is a design target only; it is never
 * loaded by the game.
 */

import { CREATURE_STATES, STATE_FILE_SUFFIX, type CreatureStateId } from '../creatureState'

export const ASSET_ROOT = '/assets'

/**
 * Extensions are probed in this order. WebP is preferred for production art;
 * SVG is last so hand-authored placeholder vectors keep working.
 */
export const IMAGE_EXTENSIONS = ['webp', 'png', 'avif', 'jpg', 'svg'] as const
export type ImageExtension = (typeof IMAGE_EXTENSIONS)[number]

export const CREATURE_ART_DIR = 'creatures'
export const WILD_ART_DIR = 'wild'
export const SHELTER_ART_DIR = 'shelters'
export const ITEM_ART_DIR = 'items'
export const EFFECT_ART_DIR = 'effects'
export const UI_ART_DIR = 'ui'
export const AUDIO_DIR = 'audio'

function withExtensions(basePath: string): string[] {
  return IMAGE_EXTENSIONS.map((ext) => `${basePath}.${ext}`)
}

function join(...parts: string[]): string {
  return [ASSET_ROOT, ...parts].join('/')
}

// ---------------------------------------------------------------------------
// Creature art
// ---------------------------------------------------------------------------

/** `dog_idle.webp`, `dog_sleeping.png`, `dog_happy.svg`, ... */
export function creatureStateCandidates(
  speciesKey: string,
  state: CreatureStateId,
): string[] {
  const suffix = STATE_FILE_SUFFIX[state]
  return withExtensions(
    join(CREATURE_ART_DIR, speciesKey, `${speciesKey}_${suffix}`),
  )
}

/** The guaranteed fallback every species should eventually ship. */
export function creatureIdleCandidates(speciesKey: string): string[] {
  return creatureStateCandidates(speciesKey, 'IDLE')
}

/**
 * Future mutation layers. A hybrid is assembled from compatible layers rather
 * than from thousands of pre-rendered combinations:
 *
 *   creatures/dog/layers/eyes_viper.webp
 *   creatures/dog/layers/back_crow_wings.webp
 *   creatures/dog/layers/skin_wolf_fur.webp
 */
export function creatureLayerCandidates(
  speciesKey: string,
  slot: string,
  layerKey: string,
): string[] {
  const slotKey = slot.toLowerCase()
  const layer = layerKey.toLowerCase()
  return withExtensions(join(CREATURE_ART_DIR, speciesKey, 'layers', `${slotKey}_${layer}`))
}

/** Condition overlays (dirt, wounds, corruption) applied above the body. */
export function creatureOverlayCandidates(overlayKey: string): string[] {
  return withExtensions(join(CREATURE_ART_DIR, 'overlays', overlayKey))
}

// ---------------------------------------------------------------------------
// Wild creatures (future Adventure)
// ---------------------------------------------------------------------------

export function wildStateCandidates(wildKey: string, state: CreatureStateId): string[] {
  const suffix = STATE_FILE_SUFFIX[state]
  return withExtensions(join(WILD_ART_DIR, wildKey, `${wildKey}_${suffix}`))
}

// ---------------------------------------------------------------------------
// Shelter art
//
// The shelter is always rendered as separate layers so the creature stays
// independent of its environment:
//
//   background -> structures -> interactables -> creature -> lighting -> UI
// ---------------------------------------------------------------------------

export type ShelterLayer =
  | 'background'
  | 'structures'
  | 'furniture'
  | 'props'
  | 'foreground'
  | 'lighting'

export function shelterLayerCandidates(shelterKey: string, layer: ShelterLayer): string[] {
  return withExtensions(join(SHELTER_ART_DIR, shelterKey, `shelter_${layer}`))
}

export const STARTER_SHELTER_KEY = 'starter'

// ---------------------------------------------------------------------------
// Items, effects and UI chrome
// ---------------------------------------------------------------------------

export function itemIconCandidates(itemKey: string): string[] {
  return withExtensions(join(ITEM_ART_DIR, itemKey))
}

export function effectCandidates(effectKey: string): string[] {
  return withExtensions(join(EFFECT_ART_DIR, effectKey))
}

export function uiAssetCandidates(uiKey: string): string[] {
  return withExtensions(join(UI_ART_DIR, uiKey))
}

export function audioCandidates(audioKey: string, ext: 'mp3' | 'ogg' = 'mp3'): string[] {
  // Audio is optional in this milestone; a missing file is never an error.
  return [`${join(AUDIO_DIR, `${audioKey}.${ext}`)}`, join(AUDIO_DIR, `${audioKey}.ogg`)]
}

// ---------------------------------------------------------------------------
// Documentation helper - the folder contract for art delivery
// ---------------------------------------------------------------------------

export interface AssetFolderSpec {
  folder: string
  purpose: string
  pattern: string
  states?: readonly CreatureStateId[]
}

export const CREATURE_STATE_LIST = CREATURE_STATES

export const STARTER_SPECIES_KEYS = ['dog', 'cat', 'bird', 'snake'] as const
export const WILD_SPECIES_KEYS = ['wolf', 'lynx', 'crow', 'viper'] as const

export const ASSET_FOLDER_SPECS: AssetFolderSpec[] = [
  {
    folder: 'creatures/<species>/',
    purpose: 'Player creature art, one folder per species.',
    pattern: '<species>_idle.webp, <species>_happy.webp, <species>_hungry.webp, ...',
    states: CREATURE_STATES,
  },
  {
    folder: 'creatures/<species>/layers/',
    purpose: 'Future mutation layers composited on top of the base body.',
    pattern: '<slot>_<mutation>.webp e.g. eyes_viper.webp',
  },
  {
    folder: 'creatures/overlays/',
    purpose: 'Condition overlays that work for every species.',
    pattern: 'dirt.webp, wounds.webp, corruption.webp, frost.webp',
  },
  {
    folder: 'wild/<species>/',
    purpose: 'Future wild creatures for Adventure encounters.',
    pattern: 'wolf_idle.webp, wolf_attack.webp, wolf_hurt.webp',
  },
  {
    folder: 'shelters/starter/',
    purpose: 'Shelter scene layers, kept separate from the creature.',
    pattern: 'shelter_background.webp, shelter_lighting.webp, ...',
  },
  { folder: 'items/', purpose: 'Inventory item icons.', pattern: 'basic_meat.webp' },
  { folder: 'effects/', purpose: 'Particle/impact art used by the shelter.', pattern: 'eat.webp' },
  { folder: 'ui/', purpose: 'Frames, sigils, textures, cursors.', pattern: 'frame_bone.webp' },
  { folder: 'audio/', purpose: 'Optional ambience and UI audio.', pattern: 'ambience_shelter.mp3' },
]
